import React, { useState, useEffect, useCallback } from 'react';
import gateApi from '../api';
import { useAuth } from '../context/AuthContext';

function todayISO() {
  return new Date().toLocaleDateString('en-CA');
}
function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toLocaleDateString('en-CA');
}

// Capped at 4 days back — a wider range means fetching (and rendering) a lot
// of full-size images at once, which gets slow fast. One button per day.
const DAY_PRESETS = [
  { id: 0, label: 'Today' },
  { id: 1, label: daysAgoLabel(1) },
  { id: 2, label: daysAgoLabel(2) },
  { id: 3, label: daysAgoLabel(3) },
];

function daysAgoLabel(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

// Separate, narrow view — PHOTO_REVIEWER never sees the regular dashboard,
// so this page owns its own minimal header rather than reusing Header/TabBar.
export default function PhotoReview() {
  const { user, logout } = useAuth();

  const [preset, setPreset] = useState(0);
  const [from, setFrom] = useState(todayISO());
  const [to, setTo]     = useState(todayISO());
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  function applyPreset(daysBack) {
    setPreset(daysBack);
    const d = daysAgoISO(daysBack);
    setFrom(d); setTo(d);
    setPage(0);
  }

  // plate search only fires on explicit submit (button or Enter), not as-you-type
  function runSearch() {
    setDebounced(search.trim());
    setPage(0);
  }

  function clearSearch() {
    setSearch('');
    setDebounced('');
    setPage(0);
  }

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setData(await gateApi.getPhotos({ from, to, search: debounced, page, size: 24 })); }
    catch (e) { setErr(e.response?.data?.error || 'Could not load photos.'); }
    finally { setLoading(false); }
  }, [from, to, debounced, page]);

  useEffect(() => { load(); }, [load]);

  const canPrev = page > 0;
  const canNext = data ? page < (data.totalPages - 1) : false;

  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 18px' };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-light)', maxWidth: 720, margin: '0 auto' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg-card)',
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Photo Review</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{user?.fullName} · {user?.role}</div>
        </div>
        <button onClick={logout} style={{
          padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)',
          background: '#fff', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Sign out</button>
      </div>

      <div style={{ padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {DAY_PRESETS.map(p => (
            <button key={p.id} onClick={() => applyPreset(p.id)} style={{
              padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${preset === p.id ? '#185FA5' : 'var(--border)'}`,
              background: preset === p.id ? '#E6F1FB' : '#fff',
              color: preset === p.id ? '#0C447C' : 'var(--text-muted)',
            }}>{p.label}</button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <input
              type="text"
              placeholder="Search plate number…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '11px 36px 11px 14px',
                border: '1px solid var(--border)', borderRadius: 10, fontSize: 14,
                background: '#fff', color: 'var(--text-primary)', outline: 'none',
                fontFamily: 'var(--font-mono)', letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            />
            {search && (
              <button
                onClick={clearSearch}
                aria-label="Clear search"
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  width: 22, height: 22, borderRadius: '50%', border: 'none', cursor: 'pointer',
                  background: 'var(--bg-muted)', color: 'var(--text-muted)', fontSize: 13,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                }}
              >✕</button>
            )}
          </div>
          <button onClick={runSearch} style={{
            padding: '0 18px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
            border: '1px solid #185FA5', background: '#185FA5', color: '#fff',
          }}>Search</button>
        </div>

        {err && <div style={{ ...card, borderColor: '#F7C1C1', background: '#FCEBEB', color: '#791F1F', fontSize: 13 }}>{err}</div>}

        {loading ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '24px 4px' }}>Loading…</div>
        ) : !data || data.rows.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '36px 20px' }}>
            No photos {debounced && `matching "${debounced}" `}between {data?.from ?? from} and {data?.to ?? to}.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {data.totalPhotos} photo{data.totalPhotos === 1 ? '' : 's'}
              {data.totalPages > 1 && ` · page ${page + 1} of ${data.totalPages}`}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
              {data.rows.map(p => (
                <div key={p.photoId} style={card}>
                  <div
                    onClick={() => window.open(p.s3Url, '_blank', 'noopener')}
                    title="Click for full-size image"
                    style={{
                      background: 'var(--bg-muted)', borderRadius: 10, overflow: 'hidden',
                      minHeight: 140, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: 10, cursor: 'zoom-in',
                    }}>
                    <img src={p.thumbUrl || p.s3Url} alt="captured load" loading="lazy"
                      style={{ width: '100%', maxHeight: 200, objectFit: 'contain', display: 'block' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{fmtTime(p.capturedAt)}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                    {p.licensePlate || (
                      <span style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>Unassigned</span>
                    )}
                  </div>
                  {p.aiGuess && (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      AI: {p.aiGuess}{p.aiConfidence != null && ` (${Number(p.aiConfidence).toFixed(0)}%)`}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {data.totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <button
                  onClick={() => canPrev && setPage(p => p - 1)}
                  disabled={!canPrev}
                  style={pageBtn(canPrev)}
                >← Prev</button>

                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  {page + 1} / {data.totalPages}
                </span>

                <button
                  onClick={() => canNext && setPage(p => p + 1)}
                  disabled={!canNext}
                  style={pageBtn(canNext)}
                >Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function pageBtn(enabled) {
  return {
    padding: '9px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    background: enabled ? '#fff' : '#F5F4F0',
    color: enabled ? 'var(--text-primary)' : 'var(--text-muted)',
    border: '1px solid var(--border)',
    cursor: enabled ? 'pointer' : 'not-allowed',
  };
}
